import Table from 'admin/components/atoms/Table';
import ClearingBadge from 'admin/components/shared/ClearingBadge';
import OffsetStatusBadge from 'admin/components/shared/OffsetStatusBadge';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import CSSModules from 'react-css-modules';
import { Link } from 'react-router-dom';
import {
  formatUUID,
  isBankAccountUnregistered,
  isPaymentClearing,
} from '~/javascripts/admin/utils';
import PaymentNoticeDownloadButton from '../../../shared/PaymentNoticeDownloadButton';
import ClearingSettleButton from '../ClearingSettleButton';
import styles from './styles.scss';

// 数値を表示。null/undefined（その行種別に存在しない派生値）は「—」を表示する。
const formatAmount = (value) =>
  typeof value === 'number' ? value.toLocaleString() : '—';

// 相殺で残高0（完全相殺）になった未精算の請求は、対応する支払の精算で自動解消される（カスケード）。
// 手動精算の導線（ボタン/チェックボックス）を出さず「相殺で解消」表示にして、誤って
// 外部回収扱いで精算済化するのを防ぐ。
const isOffsetResolved = (c) => !c.done_at && c.offset_status === 'fully_offset';

// 予定相殺（充当先の支払が未確定）が付いた請求データ。直接「精算済にする」と、実振込側で
// 既に天引き済みの債権を外部回収扱いで二重回収してしまう（BE も clearing_invoice_has_pending_offset
// で拒否）。そのため精算導線を出さず「先に相殺を外す」運用へ誘導する。
// 予定相殺額は請求行にのみ存在する派生値なので pending_offset_amount > 0 で判定できる。
const hasPendingOffset = (c) => (c.pending_offset_amount ?? 0) > 0;

const ClearingListTable = ({
  clearings,
  selectedMoneyId,
  onFinishClearing,
  selectable,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelectableSelected,
  selectableCount,
}) => {
  // 相殺運用をしない発行体（請求書を送って愚直に精算する運用）では債権相殺の列はノイズになるため、
  // 実際に相殺(確定/予定)がある行が1件でもある場合のみ相殺関連の列を表示する。
  const showOffsetColumns = clearings.some(
    (c) => (c.offset_amount ?? 0) > 0 || (c.pending_offset_amount ?? 0) > 0
  );

  const columns = [
    { label: '種別', size: 1.2 },
    { label: 'ID', size: 1 },
    { label: '決済加盟店', size: 2 },
    { label: '前回精算日', size: 1 },
    { label: '締め日', size: 1 },
    { label: '請求金額合計', size: 1.5, align: 'right' },
    { label: '支払金額', size: 1.5, align: 'right' },
    { label: '精算金額', size: 1.5, align: 'right' },
    ...(showOffsetColumns
      ? [
          { label: '債権相殺額', size: 1.5, align: 'right' },
          { label: '実振込額', size: 1.5, align: 'right' },
          { label: '債権残高', size: 1.5, align: 'right' },
        ]
      : []),
    { label: '精算状態', size: 1.5 },
    { label: '支払通知書', size: 1.5, align: 'center' },
  ];

  const totalAmount = (clearing) => {
    const clearing_amount = clearing.payment_amount - clearing.invoice_amount;
    if (clearing_amount > 0) {
      return (
        <span className={styles['positive_amount']}>
          {Math.abs(clearing_amount).toLocaleString()}
        </span>
      );
    } else if (clearing_amount < 0) {
      return (
        <span className={styles['negative_amount']}>
          {Math.abs(clearing_amount).toLocaleString()}
        </span>
      );
    }
    return <span>{Math.abs(clearing_amount)}</span>;
  };

  return (
    <div className={styles[showOffsetColumns ? 'table-scroll-wide' : 'table-scroll']}>
      <Table>
        <Table.Header>
          <Table.Row>
            {selectable && (
              <Table.Cell size={0.5} wordBreak="keep-all">
                <input
                  type="checkbox"
                  aria-label="未精算をすべて選択"
                  checked={allSelectableSelected}
                  disabled={selectableCount === 0}
                  onChange={onToggleSelectAll}
                />
              </Table.Cell>
            )}
            {columns.map((c, i) => (
              <Table.Cell key={c.label || i} size={c.size} align={c.align}>
                {c.label}
              </Table.Cell>
            ))}
          </Table.Row>
        </Table.Header>
      <Table.Body>
        {clearings.map((c) => (
          <Table.Row key={c.id}>
            {selectable && (
              <Table.Cell size={0.5} wordBreak="keep-all">
                {/* 選択できるのは未精算かつ相殺で解消されない行のみ。
                    予定相殺付きの請求は一括精算でも BE に拒否されるため選択不可にする。 */}
                {!c.done_at && !isOffsetResolved(c) && !hasPendingOffset(c) ? (
                  <input
                    type="checkbox"
                    aria-label="この精算データを選択"
                    checked={selectedIds ? selectedIds.has(c.id) : false}
                    onChange={() => onToggleSelect(c.id)}
                  />
                ) : null}
              </Table.Cell>
            )}
            <Table.Cell wordBreak="keep-all">
              <ClearingBadge clearing={c} styleName="badge" />
            </Table.Cell>
            <Table.Cell wordBreak="keep-all">
              <Link to={`private_money/${selectedMoneyId}/clearings/${c.id}`}>
                {formatUUID(c.id)}
              </Link>
            </Table.Cell>
            <Table.Cell wordBreak="break-word">
              {c.claimed_organization ? c.claimed_organization.name : ''}
              {/* 口座情報が未登録だと振込データから除外される。振込データ作成時ではなく
                  一覧で気づけるようにし、加盟店の編集画面へ直接遷移できるようにする。 */}
              {isBankAccountUnregistered(c) && c.claimed_organization && (
                <div className={styles['missing-bank-account']}>
                  <Link to={`/organizations/${c.claimed_organization.code}`}>
                    口座情報未登録
                  </Link>
                </div>
              )}
            </Table.Cell>
            <Table.Cell>{moment(c.from).format('YYYY/MM/DD')}</Table.Cell>
            <Table.Cell>{moment(c.to).format('YYYY/MM/DD')}</Table.Cell>
            <Table.Cell align="right">{c.invoice_amount.toLocaleString()}</Table.Cell>
            <Table.Cell align="right">{c.payment_amount.toLocaleString()}</Table.Cell>
            <Table.Cell align="right">{totalAmount(c)}</Table.Cell>
            {showOffsetColumns && (
              <>
                <Table.Cell align="right">
                  <div className={styles['amount-with-badge']}>
                    {c.offset_status === 'with_offset' && (
                      <OffsetStatusBadge status="with_offset" small />
                    )}
                    <span>{formatAmount(c.offset_amount)}</span>
                  </div>
                </Table.Cell>
                <Table.Cell align="right">{formatAmount(c.actual_transfer_amount)}</Table.Cell>
                <Table.Cell align="right">
                  <div className={styles['amount-with-badge']}>
                    {(c.offset_status === 'partially_offset' ||
                      c.offset_status === 'fully_offset') && (
                      <OffsetStatusBadge status={c.offset_status} small />
                    )}
                    <span>{formatAmount(c.receivable_balance)}</span>
                  </div>
                </Table.Cell>
              </>
            )}
            <Table.Cell>
              <span>
                {isOffsetResolved(c) ? (
                  <span
                    className={styles['offset-resolved']}
                    title="対応する支払の精算で自動的に精算済になります。手動での精算は不要です。"
                  >
                    相殺で解消
                  </span>
                ) : hasPendingOffset(c) ? (
                  <span
                    className={styles['offset-resolved']}
                    title="予定相殺があるため直接は精算できません。詳細画面で相殺を外してから精算してください。"
                  >
                    相殺予定あり
                  </span>
                ) : (
                  <ClearingSettleButton
                    isSettled={!!c.done_at}
                    clearingId={c.id}
                    onFinishClearing={onFinishClearing}
                  />
                )}
              </span>
            </Table.Cell>
            <Table.Cell align="center" wordBreak="keep-all">
              {/* 支払通知書は「支払」種別の行のみ。請求側には出さない。 */}
              {isPaymentClearing(c) ? (
                <PaymentNoticeDownloadButton
                  clearingId={c.id}
                  privateMoneyId={selectedMoneyId}
                  small
                />
              ) : null}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
      </Table>
    </div>
  );
};

ClearingListTable.propTypes = {
  clearings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      tel: PropTypes.string,
      registered_at: PropTypes.string,
      last_transaction_at: PropTypes.string,
    })
  ),
  selectedMoneyId: PropTypes.string.isRequired,
  selectable: PropTypes.bool,
  selectedIds: PropTypes.instanceOf(Set),
  onToggleSelect: PropTypes.func,
  onToggleSelectAll: PropTypes.func,
  allSelectableSelected: PropTypes.bool,
  selectableCount: PropTypes.number,
};

ClearingListTable.defaultProps = {
  clearings: [],
  selectable: false,
  selectedIds: null,
  onToggleSelect: () => {},
  onToggleSelectAll: () => {},
  allSelectableSelected: false,
  selectableCount: 0,
};

export default CSSModules(ClearingListTable, styles);
